import CryptoJS from 'crypto-js'

import api from '@/api'
import { KEY_BARGE_ID_NAME, KEY_CHARTER_TYPE, KEY_TEMP_CONTRACT, KEY_TUG_ID_NAME } from '@/fixtures/Constants'
import { CRYPTO_KEY } from '@/fixtures/Hash'

const encCharterTypeKey = btoa(KEY_CHARTER_TYPE)
const tugIdNameKey = btoa(KEY_TUG_ID_NAME)
const bargeIdNameKey = btoa(KEY_BARGE_ID_NAME)
const encTempContractKey = btoa(KEY_TEMP_CONTRACT)

const namespaced = true

const state = {
  contracts: [],
  tempContract: localStorage.getItem(encTempContractKey) || null,
  contractDetail: null,
  charterType: localStorage.getItem(encCharterTypeKey) || null,
  underCompanies: [],
  bargeSizes: [],
  routes: [],
  contractors: [],
  currencies: [],
  barges: [],
  tugs: [],
  bargeIdName: localStorage.getItem(bargeIdNameKey) || null,
  tugIdName: localStorage.getItem(tugIdNameKey) || null,
  bargeCertificate: [],
  tugCertificate: [],
  attachment: null,
  isDeleteAttachmentSuccess: false,
  contractNotifications: [],
  certificateNotifications: [],
  contractLog: []
}

const getters = {
  contracts: state => state.contracts,
  tempContract: state => state.tempContract
    ? JSON.parse(CryptoJS.AES.decrypt(state.tempContract, CRYPTO_KEY).toString(CryptoJS.enc.Utf8))
    : null,
  contractDetail: state => state.contractDetail,
  charterType: state => state.charterType
    ? CryptoJS.AES.decrypt(state.charterType, CRYPTO_KEY).toString(CryptoJS.enc.Utf8)
    : null,
  underCompanies: state => state.underCompanies,
  bargeSizes: state => state.bargeSizes,
  routes: state => state.routes,
  contractors: state => state.contractors,
  currencies: state => state.currencies,
  barges: state => state.barges,
  tugs: state => state.tugs,
  bargeIdName: state => state.bargeIdName
    ? JSON.parse(CryptoJS.AES.decrypt(state.bargeIdName, CRYPTO_KEY).toString(CryptoJS.enc.Utf8))
    : null,
  tugIdName: state => state.tugIdName
    ? JSON.parse(CryptoJS.AES.decrypt(state.tugIdName, CRYPTO_KEY).toString(CryptoJS.enc.Utf8))
    : null,
  bargeCertificate: state => state.bargeCertificate,
  tugCertificate: state => state.tugCertificate,
  attachment: state => state.attachment,
  isDeleteAttachmentSuccess: state => state.isDeleteAttachmentSuccess,
  contractNotifications: state => state.contractNotifications,
  certificateNotifications: state => state.certificateNotifications,
  contractLog: state => state.contractLog
}

const mutations = {
  SET_CONTRACTS: (state, payload) => {
    state.contracts = payload
  },
  SET_TEMP_CONTRACT: (state, payload) => {
    state.tempContract = payload
    localStorage.setItem(encTempContractKey, payload)
  },
  SET_CONTRACT_DETAIL: (state, payload) => {
    state.contractDetail = payload
  },
  SET_CHARTER_TYPE: (state, payload) => {
    state.charterType = payload
    localStorage.setItem(encCharterTypeKey, payload)
  },
  SET_UNDER_COMPANIES: (state, payload) => {
    state.underCompanies = payload
  },
  SET_BARGE_SIZES: (state, payload) => {
    state.bargeSizes = payload
  },
  SET_ROUTES: (state, payload) => {
    state.routes = payload
  },
  SET_CONTRACTORS: (state, payload) => {
    state.contractors = payload
  },
  SET_CURRENCIES: (state, payload) => {
    state.currencies = payload
  },
  SET_BARGES: (state, payload) => {
    state.barges = payload
  },
  SET_TUGS: (state, payload) => {
    state.tugs = payload
  },
  SET_BARGE_ID_NAME: (state, payload) => {
    state.bargeName = payload
    localStorage.setItem(bargeIdNameKey, payload)
  },
  SET_TUG_ID_NAME: (state, payload) => {
    state.tugName = payload
    localStorage.setItem(tugIdNameKey, payload)
  },
  SET_BARGE_CERTIFICATE: (state, payload) => {
    state.bargeCertificate = payload
  },
  SET_TUG_CERTIFICATE: (state, payload) => {
    state.tugCertificate = payload
  },
  SET_ATTACHMENT: (state, payload) => {
    state.attachment = payload
  },
  SET_DELETE_ATTACHMENT_SUCCESS: (state, payload) => {
    state.isDeleteAttachmentSuccess = payload
  },
  SET_CONTRACT_NOTIFICATIONS: (state, payload) => {
    state.contractNotifications = payload
  },
  SET_CERTIFICATE_NOTIFICATIONS: (state, payload) => {
    state.certificateNotifications = payload
  },
  SET_CONTRACT_LOG: (state, payload) => {
    state.contractLog = payload
  }
}

const actions = {
  async fetchContracts({ commit, rootGetters }, payload) {
    try {
      const filterConfig = rootGetters['Table/defaultConfig']
      const { type, ...customPayload } = payload

      const filter = {
        search: filterConfig.search,
        page: filterConfig.currentPage,
        limit: filterConfig.pageSize,
        ...(payload.type === 'ALL' ? customPayload : payload)
      }
      const { data } = await api.getContracts(filter)
      if (data.error) {
        commit('SET_CONTRACTS', [])
        commit('Table/SET_TOTAL_DATA', 0, { root: true })
        commit('Table/SET_TOTAL_PAGE', 1, { root: true })
        return
      }

      commit('SET_CONTRACTS', data.data)
      commit('Table/SET_TOTAL_DATA', data.total, { root: true })
      commit('Table/SET_TOTAL_PAGE', data.pages, { root: true })
    } catch (err) {
      throw err
    }
  },
  async fetchMasters({ commit, dispatch }, payload) {
    try {
      const { data } = await api.getMasterDropdown({ type: payload.toUpperCase() })
      const commitKey = await dispatch('setMasterCommitKey', payload)

      if (data.error) {
        commit(commitKey, [])
        return
      }

      commit(commitKey, data)
    } catch (err) {
      throw err
    }
  },
  async fetchBargeCertificates({ commit }, payload) {
    try {
      const { data } = await api.getBargeCertificates(payload)
      if (data.error) {
        commit('SET_BARGE_CERTIFICATE', [])
        return
      }
      commit('SET_BARGE_CERTIFICATE', data)
    } catch (err) {
      throw err
    }
  },
  async fetchTugCertificates({ commit }, payload) {
    try {
      const { data } = await api.getTugCertificates(payload)
      if (data.error) {
        commit('SET_TUG_CERTIFICATE', [])
        return
      }
      commit('SET_TUG_CERTIFICATE', data)
    } catch (err) {
      throw err
    }
  },
  async postBargeCertificates(_, payload) {
    try {
      const { id, form } = payload
      await api.postBargeCertificates(id, form)
    } catch (err) {
      throw err
    }
  },
  async postTugCertificates(_, payload) {
    try {
      const { id, form } = payload
      await api.postTugCertificates(id, form)
    } catch (err) {
      throw err
    }
  },
  async fetchBarges({ commit }, payload) {
    try {
      const { data } = await api.getBargeList(payload)
      if (data.error) {
        commit('SET_BARGES', [])
        return
      }

      commit('SET_BARGES', data)
    } catch (err) {
      throw err
    }
  },
  async fetchTugs({ commit }, payload) {
    try {
      const { data } = await api.getTugList(payload)
      if (data.error) {
        commit('SET_TUGS', [])
        return
      }

      commit('SET_TUGS', data)
    } catch (err) {
      throw err
    }
  },
  async uploadAttachment({ commit }, payload) {
    try {
      const formData = new FormData()
      formData.append('attachment', payload)

      const { data } = await api.uploadAttachment(formData)
      if (data.error) {
        commit('SET_ATTACHMENT', null)
        return
      }
      commit('SET_DELETE_ATTACHMENT_SUCCESS', false)
      commit('SET_ATTACHMENT', data)
    } catch (err) {
      throw err
    }
  },
  async deleteAttachment({ commit }, payload) {
    try {
      const { data } = await api.deleteAttachment(payload)
      if (data.error) {
        commit('SET_DELETE_ATTACHMENT_SUCCESS', false)
        return
      }

      commit('SET_DELETE_ATTACHMENT_SUCCESS', true)
    } catch (err) {
      throw err
    }
  },
  async postContract(_, payload) {
    try {
      await api.postContract(payload)
    } catch (err) {
      throw err
    }
  },
  async putContract(_, payload) {
    try {
      const id = payload.id
      delete payload.id

      await api.putContract(id, payload)
    } catch (err) {
      throw err
    }
  },
  async deleteContract(_, payload) {
    try {
      await api.deleteContract(payload)
    } catch (err) {
      throw err
    }
  },
  async fetchContractDetail({ commit }, payload) {
    try {
      const { data } = await api.getContractDetail(payload)

      if (data.error) {
        commit('SET_CONTRACT_DETAIL', null)
        return
      }

      commit('SET_CONTRACT_DETAIL', data)
    } catch (err) {
      throw err
    }
  },
  async fetchContractNotifications({ commit }, payload) {
    try {
      const { data } = await api.getContractNotification(payload)

      if (data.error) {
        commit('SET_CONTRACT_NOTIFICATIONS', [])
        return
      }

      commit('SET_CONTRACT_NOTIFICATIONS', data)
    } catch (err) {
      throw err
    }
  },
  async fetchCertificateNotifications({ commit }, payload) {
    try {
      const { data } = await api.getCertificateNotification(payload)

      if (data.error) {
        commit('SET_CERTIFICATE_NOTIFICATIONS', [])
        return
      }

      commit('SET_CERTIFICATE_NOTIFICATIONS', data)
    } catch (err) {
      throw err
    }
  },
  async fetchContractLog({ commit }, payload) {
    try {
      const { data } = await api.getContractLog(payload)
      if (data.error) {
        commit('SET_CONTRACT_LOG', [])
        return
      }

      commit('SET_CONTRACT_LOG', data)
    } catch (err) {
      throw err
    }
  },
  async putBargeActiveState(_, payload) {
    try {
      const id = payload.id
      delete payload.id

      await api.putBargeActiveState(id, payload)
    } catch (err) {
      throw err
    }
  },
  async putTugActiveState(_, payload) {
    try {
      const id = payload.id
      delete payload.id

      await api.putTugActiveState(id, payload)
    } catch (err) {
      throw err
    }
  },
  setTempContract({ commit }, payload) {
    const encTempContract = CryptoJS.AES.encrypt(JSON.stringify(payload), CRYPTO_KEY).toString()
    commit('SET_TEMP_CONTRACT', encTempContract)
  },
  removeTempContract({ commit }) {
    commit('SET_TEMP_CONTRACT', null)
    localStorage.removeItem(encTempContractKey)
  },
  setCharterType({ commit }, payload) {
    commit('SET_CHARTER_TYPE', CryptoJS.AES.encrypt(payload, CRYPTO_KEY).toString())
  },
  setMasterCommitKey(_, payload) {
    switch (payload) {
      case 'under_company':
        return 'SET_UNDER_COMPANIES'
      case 'barge_size':
        return 'SET_BARGE_SIZES'
      case 'route':
        return 'SET_ROUTES'
      case 'contractor':
        return 'SET_CONTRACTORS'
      case 'currency':
        return 'SET_CURRENCIES'
    }
  },
  setBargeIdName({ commit }, payload) {
    commit('SET_BARGE_ID_NAME', CryptoJS.AES.encrypt(JSON.stringify(payload), CRYPTO_KEY).toString())
  },
  setTugIdName({ commit }, payload) {
    commit('SET_TUG_ID_NAME', CryptoJS.AES.encrypt(JSON.stringify(payload), CRYPTO_KEY).toString())
  }
}

export default { namespaced, state, getters, mutations, actions }
